/**
 * v3.18-1295 — Communication Contact Directory page.
 *
 * Authoritative Hub-wide contact directory. Supports unlimited
 * contacts, multiple phones + emails, groups, favorites, search,
 * drag-and-drop ordering, notes, communication history, and
 * click-to-call / SMS / email — every action displays the contact's
 * local time via <ClientLocalTime>.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Users, Plus, Search, Star, Trash2, Pencil, Phone, MessageSquare,
  Mail, GripVertical, Tag, Filter, Upload, Download,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import ContactEditorDialog from '../components/ContactEditorDialog';
import ContactImportDialog from '../components/ContactImportDialog';
import ClientLocalTime from '../components/ClientLocalTime';
import {
  listContacts, deleteContact, updateContact, reorderContacts,
  listGroups, createGroup, deleteGroup,
  primaryPhone, primaryEmail, humanizeAgo,
} from '../lib/communication_contacts';
import api from '../lib/api';
import { canUsePhoneSystem } from '../lib/phone_access';
import { dispatchDial, dispatchCompose } from '../lib/comm_bus';

const useMe = () => {
  const [me, setMe] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/auth/me');
        if (!cancelled) setMe(data);
      } catch (_e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, []);
  return me;
};

const useCompanies = () => {
  const [companies, setCompanies] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/companies');
        const rows = Array.isArray(data) ? data : (data?.companies || data?.items || []);
        setCompanies(rows);
      } catch (_e) { setCompanies([]); }
    })();
  }, []);
  return companies;
};

const GroupPill = ({ group, active, onClick, onDelete, canWrite }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full text-xs cursor-pointer transition
      ${active ? 'text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
    style={active ? { backgroundColor: group.color || '#6366F1' } : undefined}
    data-testid={`group-pill-${group.id}`}
  >
    <button
      type="button"
      onClick={onClick}
      className="pl-2 py-0.5"
    >
      {group.name}
    </button>
    {canWrite && (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        className="pr-2 py-0.5 hover:text-red-500"
        title="Delete group"
        data-testid={`group-pill-delete-${group.id}`}
      >
        <Trash2 className="h-3 w-3" />
      </button>
    )}
  </span>
);

const ContactRow = ({
  contact, groups, me, onEdit, onDelete, onToggleFavorite,
  onDragStart, onDragOver, onDrop, dragIdx, index,
}) => {
  const p = primaryPhone(contact);
  const e = primaryEmail(contact);
  const phoneEnabled = canUsePhoneSystem(me);
  const dragging = dragIdx === index;

  const contactGroups = (contact.group_ids || [])
    .map((gid) => groups.find((g) => g.id === gid))
    .filter(Boolean);

  const call = (phone) => {
    const num = phone?.number;
    if (!num) return;
    if (phone?.do_not_call) {
      toast.error('Do Not Call flag set on this number');
      return;
    }
    if (!phoneEnabled) {
      toast.info('Phone system access not enabled for your company');
      return;
    }
    dispatchDial({
      number: num,
      contact_id: contact.id,
      client_id: contact.client_id,
    });
  };
  const sms = (phone) => {
    const num = phone?.number;
    if (!num) return;
    if (phone?.do_not_call) {
      toast.error('Do Not Call flag set on this number');
      return;
    }
    if (!phoneEnabled) {
      toast.info('Phone system access not enabled for your company');
      return;
    }
    window.location.href = `sms:${num.replace(/[^0-9+]/g, '')}`;
  };
  const mailto = (email) => {
    if (!email?.address) return;
    dispatchCompose({
      to: email.address,
      contact_id: contact.id,
      client_id: contact.client_id,
    });
  };

  return (
    <div
      draggable
      onDragStart={(e2) => onDragStart(e2, index)}
      onDragOver={onDragOver}
      onDrop={(e2) => onDrop(e2, index)}
      data-testid={`contact-row-${contact.id}`}
      className={`flex items-center gap-3 px-3 py-2.5 border-b border-slate-100
        hover:bg-slate-50 transition ${dragging ? 'opacity-40' : ''}`}
    >
      <div
        className="text-slate-300 cursor-grab active:cursor-grabbing shrink-0"
        title="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </div>
      <button
        type="button"
        onClick={() => onToggleFavorite(contact)}
        title={contact.is_favorite ? 'Remove favorite' : 'Mark favorite'}
        data-testid={`contact-row-fav-${contact.id}`}
        className={`shrink-0 h-6 w-6 rounded flex items-center justify-center transition
          ${contact.is_favorite ? 'text-amber-500' : 'text-slate-300 hover:text-amber-500'}`}
      >
        <Star className="h-4 w-4" fill={contact.is_favorite ? 'currentColor' : 'none'} />
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className="text-sm font-semibold text-slate-900 truncate"
            data-testid={`contact-row-name-${contact.id}`}
          >
            {contact.full_name}
          </span>
          {contact.is_primary && (
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">
              Primary
            </Badge>
          )}
          {contact.title && (
            <span className="text-xs text-slate-500 truncate">· {contact.title}</span>
          )}
          {contact.department && (
            <span className="text-xs text-slate-400 truncate">· {contact.department}</span>
          )}
        </div>
        <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
          {p && (
            <span className="text-xs text-slate-600 inline-flex items-center gap-1">
              <span className="text-slate-400 uppercase text-[10px]">{p.label}</span>
              <span className={p.do_not_call ? 'line-through text-red-500' : ''}>{p.number}</span>
              {p.is_verified && (
                <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-50 text-emerald-700 uppercase">
                  Verified
                </span>
              )}
              {p.is_preferred && (
                <span className="text-[9px] px-1 py-0.5 rounded bg-indigo-50 text-indigo-700 uppercase">
                  Preferred
                </span>
              )}
              {p.do_not_call && (
                <span className="text-[9px] px-1 py-0.5 rounded bg-red-50 text-red-700 uppercase">
                  DNC
                </span>
              )}
            </span>
          )}
          {e && (
            <span className="text-xs text-slate-600 truncate">
              <span className="text-slate-400 uppercase text-[10px] mr-1">{e.label}</span>
              {e.address}
            </span>
          )}
          {contactGroups.map((g) => (
            <span
              key={g.id}
              className="text-[10px] px-1.5 py-0.5 rounded-full text-white"
              style={{ backgroundColor: g.color || '#6366F1' }}
            >
              {g.name}
            </span>
          ))}
        </div>
        <div className="mt-0.5 flex items-center gap-3 flex-wrap">
          <ClientLocalTime
            timezone={contact.timezone || undefined}
            clientId={contact.timezone ? undefined : contact.client_id}
            variant="chip"
          />
          {contact.last_contacted_at && (
            <span className="text-[10px] text-slate-500" data-testid={`contact-row-last-${contact.id}`}>
              Last contacted {humanizeAgo(contact.last_contacted_at)}
            </span>
          )}
          {contact.last_call_at && (
            <span className="text-[10px] text-emerald-700">
              📞 {humanizeAgo(contact.last_call_at)}
            </span>
          )}
          {contact.last_sms_at && (
            <span className="text-[10px] text-blue-700">
              💬 {humanizeAgo(contact.last_sms_at)}
            </span>
          )}
          {contact.last_email_at && (
            <span className="text-[10px] text-purple-700">
              ✉️ {humanizeAgo(contact.last_email_at)}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={() => call(p)}
          disabled={!p || p?.do_not_call}
          data-testid={`contact-row-call-${contact.id}`}
          className="h-8 w-8 rounded flex items-center justify-center text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-30"
          title={p?.do_not_call ? 'Do Not Call' : 'Call'}
        >
          <Phone className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => sms(p)}
          disabled={!p || p?.do_not_call}
          data-testid={`contact-row-sms-${contact.id}`}
          className="h-8 w-8 rounded flex items-center justify-center text-slate-500 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-30"
          title={p?.do_not_call ? 'Do Not Call' : 'SMS'}
        >
          <MessageSquare className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => mailto(e)}
          disabled={!e}
          data-testid={`contact-row-email-${contact.id}`}
          className="h-8 w-8 rounded flex items-center justify-center text-slate-500 hover:bg-purple-50 hover:text-purple-600 disabled:opacity-30"
          title="Email"
        >
          <Mail className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onEdit(contact)}
          data-testid={`contact-row-edit-${contact.id}`}
          className="h-8 w-8 rounded flex items-center justify-center text-slate-500 hover:bg-slate-100"
          title="Edit"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(contact)}
          data-testid={`contact-row-delete-${contact.id}`}
          className="h-8 w-8 rounded flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-500"
          title="Delete"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const ExportMenu = ({ companyId }) => {
  const download = async (fmt) => {
    if (!companyId) { toast.error('Pick a company first'); return; }
    try {
      // v3.21-1729 · Migrated off raw fetch + wrong-key 'token' read.
      // Uses the centralized axios client so the refresh-on-401 path
      // and single-flight queue apply to file downloads too.
      const resp = await api.get(
        `/communication-contacts/exports/${fmt}`,
        { params: { company_id: companyId }, responseType: 'blob' },
      );
      const blob = resp.data;
      const dl = document.createElement('a');
      dl.href = URL.createObjectURL(blob);
      dl.download = fmt === 'csv' ? 'contacts.csv' : 'contacts.xlsx';
      document.body.appendChild(dl);
      dl.click();
      dl.remove();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Export failed');
    }
  };
  return (
    <div className="inline-flex rounded-md" data-testid="contacts-export-group">
      <Button
        variant="outline" className="rounded-r-none"
        onClick={() => download('csv')} data-testid="contacts-export-csv"
      >
        <Download className="h-4 w-4 mr-1" /> CSV
      </Button>
      <Button
        variant="outline" className="rounded-l-none border-l-0"
        onClick={() => download('xlsx')} data-testid="contacts-export-xlsx"
      >
        Excel
      </Button>
    </div>
  );
};

const NewGroupDialog = ({ open, onOpenChange, onCreated, companyId }) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#6366F1');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setName(''); setColor('#6366F1'); } }, [open]);
  const submit = async () => {
    if (!name.trim()) { toast.error('Group name is required'); return; }
    if (!companyId) { toast.error('Pick a company first'); return; }
    setSaving(true);
    try {
      const g = await createGroup({ name: name.trim(), color, company_id: companyId });
      toast.success('Group created');
      onCreated?.(g);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-group-dialog">
        <DialogHeader>
          <DialogTitle>New Contact Group</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Lenders / VIP / Investors"
              data-testid="new-group-name"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs">Color</Label>
            <div className="flex items-center gap-2">
              <input
                type="color" value={color} onChange={(e) => setColor(e.target.value)}
                className="h-9 w-14 rounded cursor-pointer border border-slate-200"
                data-testid="new-group-color"
              />
              <span className="text-xs text-slate-500">{color}</span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="new-group-save">
            {saving ? 'Saving…' : 'Create group'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CommunicationContactsPage = () => {
  const me = useMe();
  const companies = useCompanies();
  const [companyId, setCompanyId] = useState('');
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const canWrite = me && (
    ['ptc_admin', 'ptc_director', 'ptc_manager', 'administrator',
     'company_admin', 'company_director'].includes((me.role || '').toLowerCase())
    || !!me.company_id
  );

  // Default company selection from `me`.
  useEffect(() => {
    if (!companyId && me?.company_id) setCompanyId(me.company_id);
  }, [me, companyId]);

  const refresh = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [c, g] = await Promise.all([
        listContacts({ company_id: companyId, search: search || undefined,
                       group_id: selectedGroup || undefined,
                       favorites_only: tab === 'favorites',
                       with_last_contacted: true }),
        listGroups({ company_id: companyId }),
      ]);
      setContacts(c.contacts || []);
      setGroups(g.groups || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load contacts');
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [companyId, search, selectedGroup, tab]);

  const filtered = useMemo(() => {
    if (tab === 'primary') return contacts.filter((c) => c.is_primary);
    return contacts;
  }, [contacts, tab]);

  const onNew = () => { setEditing(null); setEditorOpen(true); };
  const onEdit = (c) => { setEditing(c); setEditorOpen(true); };
  const onDelete = async (c) => {
    if (!window.confirm(`Delete contact "${c.full_name}"?`)) return;
    try { await deleteContact(c.id); toast.success('Deleted'); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Delete failed'); }
  };
  const toggleFavorite = async (c) => {
    try {
      await updateContact(c.id, { is_favorite: !c.is_favorite });
      setContacts((rows) => rows.map((x) => (x.id === c.id ? { ...x, is_favorite: !c.is_favorite } : x)));
    } catch (err) { toast.error('Failed'); }
  };
  const deleteGroupClick = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"? Contacts stay but lose this tag.`)) return;
    try { await deleteGroup(g.id); toast.success('Group deleted'); refresh(); }
    catch (err) { toast.error('Failed'); }
  };

  // Drag & drop reorder — only while showing the unfiltered "all" tab.
  const canReorder = tab === 'all' && !selectedGroup && !search;
  const onDragStart = (e, i) => { if (!canReorder) return; setDragIdx(i); e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e) => { if (!canReorder) return; e.preventDefault(); };
  const onDrop = async (e, i) => {
    if (!canReorder || dragIdx === null) return;
    e.preventDefault();
    if (i === dragIdx) { setDragIdx(null); return; }
    const next = [...filtered];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    setContacts(next);
    setDragIdx(null);
    try {
      await reorderContacts(next.map((c) => c.id));
    } catch (_err) { /* silent */ }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="communication-contacts-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2 text-slate-900">
            <Users className="h-7 w-7 text-indigo-600" />
            Contact Directory
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Every phone number, every email, every group — one authoritative list.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <>
              <ExportMenu companyId={companyId} />
              <Button
                variant="outline"
                onClick={() => setImportOpen(true)}
                data-testid="contacts-import-btn"
              >
                <Upload className="h-4 w-4 mr-1" /> Import CSV
              </Button>
              <Button
                variant="outline"
                onClick={() => setNewGroupOpen(true)}
                data-testid="contacts-new-group-btn"
              >
                <Tag className="h-4 w-4 mr-1" /> New group
              </Button>
              <Button onClick={onNew} data-testid="contacts-new-btn">
                <Plus className="h-4 w-4 mr-1" /> New contact
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Company selector for multi-company users */}
      {companies.length > 1 && (
        <div className="mb-4 flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-400" />
          <select
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="text-sm border border-slate-200 rounded px-2 py-1"
            data-testid="contacts-company-select"
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name || c.company_name || c.id}</option>
            ))}
          </select>
        </div>
      )}

      {/* Search + tabs */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, title, phone, email…"
            className="pl-8"
            data-testid="contacts-search"
          />
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="all" data-testid="contacts-tab-all">All</TabsTrigger>
            <TabsTrigger value="favorites" data-testid="contacts-tab-favorites">
              <Star className="h-3 w-3 mr-1" /> Favorites
            </TabsTrigger>
            <TabsTrigger value="primary" data-testid="contacts-tab-primary">Primary</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Group filter row */}
      {groups.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-xs text-slate-500 font-medium uppercase tracking-wider">
            Groups:
          </span>
          <button
            type="button"
            onClick={() => setSelectedGroup('')}
            className={`px-2 py-0.5 text-xs rounded-full border transition
              ${!selectedGroup ? 'bg-slate-900 text-white border-slate-900'
                                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
            data-testid="group-filter-all"
          >
            All
          </button>
          {groups.map((g) => (
            <GroupPill
              key={g.id}
              group={g}
              active={selectedGroup === g.id}
              onClick={() => setSelectedGroup(selectedGroup === g.id ? '' : g.id)}
              onDelete={() => deleteGroupClick(g)}
              canWrite={canWrite}
            />
          ))}
        </div>
      )}

      {/* Contacts list */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-400" data-testid="contacts-loading">
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400" data-testid="contacts-empty">
            No contacts yet. {canWrite && 'Click "New contact" to add one.'}
          </div>
        ) : (
          filtered.map((c, i) => (
            <ContactRow
              key={c.id}
              contact={c}
              groups={groups}
              me={me}
              onEdit={onEdit}
              onDelete={onDelete}
              onToggleFavorite={toggleFavorite}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              dragIdx={dragIdx}
              index={i}
            />
          ))
        )}
      </div>

      <ContactEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        contact={editing}
        companyId={companyId}
        groups={groups}
        onSaved={refresh}
      />
      <NewGroupDialog
        open={newGroupOpen}
        onOpenChange={setNewGroupOpen}
        companyId={companyId}
        onCreated={() => refresh()}
      />
      <ContactImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        companyId={companyId}
        onDone={refresh}
      />
    </div>
  );
};

export default CommunicationContactsPage;
